/**
 * Core types and interfaces for TestDino CLI
 */

import { z } from 'zod';

// CLI Options Schema
export const CLIOptionsSchema = z.object({
  reportDirectory: z.string().min(1, 'Report directory is required'),
  token: z.string().min(1, 'API token is required'),
  uploadImages: z.boolean().optional().default(false),
  uploadVideos: z.boolean().optional().default(false),
  uploadHtml: z.boolean().optional().default(false),
  uploadTraces: z.boolean().optional().default(false),
  uploadFiles: z.boolean().optional().default(false),
  uploadFullJson: z.boolean().optional().default(false),
  jsonReport: z.string().optional(),
  htmlReport: z.string().optional(),
  traceDir: z.string().optional(),
  verbose: z.boolean().optional().default(false),
  environment: z.string().optional().default('unknown'),
});

export type CLIOptions = z.infer<typeof CLIOptionsSchema>;

// Configuration Schema
export const ConfigSchema = z.object({
  apiUrl: z.string().url(),
  token: z.string().min(1),
  uploadImages: z.boolean().default(false),
  uploadVideos: z.boolean().default(false),
  uploadHtml: z.boolean().default(false),
  uploadTraces: z.boolean().default(false),
  uploadFiles: z.boolean().default(false),
  uploadFullJson: z.boolean().default(false),
  verbose: z.boolean().default(false),
  // Target environment tag for upload metadata
  environment: z.string().default('unknown'),
  // Performance and upload settings
  batchSize: z.number().min(1).max(20).default(5),
  maxConcurrentUploads: z.number().min(1).max(50).default(10),
  uploadTimeout: z.number().min(5000).max(300000).default(60000),
  retryAttempts: z.number().min(1).max(10).default(3),
});

export type Config = z.infer<typeof ConfigSchema>;

// Error Types
export class BaseError extends Error {
  public readonly code: string;

  public readonly cause?: Error;

  constructor(message: string, code: string, cause?: Error) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    if (cause !== undefined) {
      this.cause = cause;
    }
    Error.captureStackTrace(this, this.constructor);
  }
}

export class ConfigurationError extends BaseError {
  constructor(message: string, cause?: Error) {
    super(message, 'CONFIG_ERROR', cause);
  }
}

export class ValidationError extends BaseError {
  constructor(message: string, cause?: Error) {
    super(message, 'VALIDATION_ERROR', cause);
  }
}

export class NetworkError extends BaseError {
  constructor(message: string, cause?: Error) {
    super(message, 'NETWORK_ERROR', cause);
  }
}

export class FileSystemError extends BaseError {
  constructor(message: string, cause?: Error) {
    super(message, 'FILESYSTEM_ERROR', cause);
  }
}

export class AuthenticationError extends BaseError {
  constructor(message: string, cause?: Error) {
    super(message, 'AUTH_ERROR', cause);
  }
}

/**
 * Usage limit error data from server response
 */
export interface UsageLimitData {
  tier: string;
  limit: number;
  used: number;
  remaining: number;
  resetDate: string;
  organizationId?: string;
}

/**
 * Error thrown when monthly test case limit is exceeded
 */
export class UsageLimitError extends BaseError {
  public readonly data: UsageLimitData;

  constructor(message: string, data: UsageLimitData, cause?: Error) {
    super(message, 'QUOTA_LIMIT_EXCEEDED', cause);
    this.data = data;
  }
}

// Exit Codes
export enum ExitCode {
  SUCCESS = 0,
  GENERAL_ERROR = 1,
  AUTHENTICATION_ERROR = 2,
  FILE_NOT_FOUND_ERROR = 3,
  NETWORK_ERROR = 4,
  USAGE_LIMIT_ERROR = 5,
}

// Command Types
export interface Command {
  readonly name: string;
  readonly description: string;
  execute(options: CLIOptions): Promise<void>;
}

// Progress Tracking
export interface ProgressTracker {
  start(message: string): void;
  update(message: string): void;
  succeed(message: string): void;
  fail(message: string): void;
  warn(message: string): void;
}

// Utility function to convert string to boolean
export const stringToBoolean = (value: string | undefined): boolean => {
  if (!value) return false;
  return ['true', '1', 'yes', 'on'].includes(value.toLowerCase());
};
